import { Platform } from 'react-native';
import { AppleReceiptFetcher } from '@/modules/AppleReceiptFetcher';
import { supabase } from '@/lib/supabase';
import AsyncStorage from '@react-native-async-storage/async-storage';

const RECEIPT_CACHE_KEY = '@apple_receipt_cache';
const RECEIPT_CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000;

interface CachedReceipt {
  receipt: string;
  timestamp: number;
}

export class AppleReceiptService {
  private static instance: AppleReceiptService;
  private lastReceipt: string | null = null;
  private lastFetchTime: number = 0;

  private constructor() {}

  static getInstance(): AppleReceiptService {
    if (!AppleReceiptService.instance) {
      AppleReceiptService.instance = new AppleReceiptService();
    }
    return AppleReceiptService.instance;
  }

  async fetchReceipt(forceRefresh = false): Promise<string | null> {
    if (Platform.OS !== 'ios') {
      console.log('🧾 [Apple Receipt] Skipping - not iOS platform');
      return null;
    }

    if (!AppleReceiptFetcher.isAvailable()) {
      console.warn('🧾 [Apple Receipt] Module not available');
      return null;
    }

    const now = Date.now();
    const timeSinceLastFetch = now - this.lastFetchTime;

    if (!forceRefresh && this.lastReceipt && timeSinceLastFetch < 60000) {
      console.log('🧾 [Apple Receipt] Using cached receipt from memory');
      return this.lastReceipt;
    }

    if (!forceRefresh) {
      const cachedReceipt = await this.getCachedReceipt();
      if (cachedReceipt) {
        console.log('🧾 [Apple Receipt] Using cached receipt from storage');
        this.lastReceipt = cachedReceipt;
        this.lastFetchTime = now;
        return cachedReceipt;
      }
    }

    try {
      console.log('🧾 [Apple Receipt] Fetching receipt from device...');
      const receipt = await AppleReceiptFetcher.fetchAppReceiptBase64();

      if (receipt) {
        this.lastReceipt = receipt;
        this.lastFetchTime = now;
        await this.cacheReceipt(receipt);
        console.log('🧾 [Apple Receipt] Receipt fetched successfully');
        return receipt;
      }

      console.warn('🧾 [Apple Receipt] No receipt available');
      return null;
    } catch (error) {
      console.error('🧾 [Apple Receipt] Error fetching receipt:', error);
      return null;
    }
  }

  async sendReceiptToBackend(
    receipt: string,
    userId: string,
    metadata?: Record<string, any>
  ): Promise<boolean> {
    try {
      console.log('🧾 [Apple Receipt] Sending receipt to backend for validation...');

      const { data, error } = await supabase.functions.invoke('validate-apple-receipt', {
        body: {
          receipt,
          userId,
          metadata,
        },
      });

      if (error) {
        console.error('🧾 [Apple Receipt] Backend validation error:', error);
        return false;
      }

      console.log('🧾 [Apple Receipt] Receipt validated successfully:', data);
      return true;
    } catch (error) {
      console.error('🧾 [Apple Receipt] Error sending receipt to backend:', error);
      return false;
    }
  }

  async storeReceiptInDatabase(
    receipt: string,
    userId: string,
    transactionId?: string
  ): Promise<boolean> {
    try {
      console.log('🧾 [Apple Receipt] Storing receipt in database...');

      const { error } = await supabase.from('apple_receipts').insert({
        user_id: userId,
        receipt_data: receipt,
        transaction_id: transactionId,
        platform: 'ios',
        created_at: new Date().toISOString(),
      });

      if (error) {
        console.error('🧾 [Apple Receipt] Database storage error:', error);
        return false;
      }

      console.log('🧾 [Apple Receipt] Receipt stored successfully');
      return true;
    } catch (error) {
      console.error('🧾 [Apple Receipt] Error storing receipt:', error);
      return false;
    }
  }

  async fetchAndValidateReceipt(
    userId: string,
    options?: {
      forceRefresh?: boolean;
      sendToBackend?: boolean;
      storeInDatabase?: boolean;
      metadata?: Record<string, any>;
    }
  ): Promise<{ success: boolean; receipt: string | null }> {
    const receipt = await this.fetchReceipt(options?.forceRefresh);

    if (!receipt) {
      return { success: false, receipt: null };
    }

    let validationSuccess = true;

    if (options?.sendToBackend) {
      const backendResult = await this.sendReceiptToBackend(
        receipt,
        userId,
        options?.metadata
      );
      validationSuccess = validationSuccess && backendResult;
    }

    if (options?.storeInDatabase) {
      const storageResult = await this.storeReceiptInDatabase(
        receipt,
        userId,
        options?.metadata?.transactionId
      );
      validationSuccess = validationSuccess && storageResult;
    }

    return { success: validationSuccess, receipt };
  }

  private async cacheReceipt(receipt: string): Promise<void> {
    try {
      const cacheData: CachedReceipt = {
        receipt,
        timestamp: Date.now(),
      };
      await AsyncStorage.setItem(RECEIPT_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('🧾 [Apple Receipt] Error caching receipt:', error);
    }
  }

  private async getCachedReceipt(): Promise<string | null> {
    try {
      const cached = await AsyncStorage.getItem(RECEIPT_CACHE_KEY);
      if (!cached) return null;

      const cacheData: CachedReceipt = JSON.parse(cached);
      const now = Date.now();

      if (now - cacheData.timestamp > RECEIPT_CACHE_EXPIRY_MS) {
        await AsyncStorage.removeItem(RECEIPT_CACHE_KEY);
        return null;
      }

      return cacheData.receipt;
    } catch (error) {
      console.error('🧾 [Apple Receipt] Error reading cached receipt:', error);
      return null;
    }
  }

  clearCache(): void {
    this.lastReceipt = null;
    this.lastFetchTime = 0;
    AsyncStorage.removeItem(RECEIPT_CACHE_KEY).catch(console.error);
  }
}

export const appleReceiptService = AppleReceiptService.getInstance();
